import {
    ChevronDown,
    ChevronUp,
    Loader2,
    MessageSquareText,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Markdown } from '@apps/web/components/ui/markdown';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    type UserQuestionActionStyle,
    type UserQuestionPresentation,
} from '@apps/web/features/phoebe_chat_v3/model/question_presentation';
import { V3RowSelectionCheckbox } from '@apps/web/features/phoebe_chat_v3/ui/row_selection_checkbox';
import { cn } from '@apps/web/lib/utils';

const ACTION_STYLE_CLASSES: Readonly<Record<UserQuestionActionStyle, string>> =
    {
        primary:
            'border-agent-action bg-agent-action text-white hover:bg-agent-action-strong focus-visible:ring-agent-action/35',
        secondary:
            'border-agent-action/45 bg-background text-agent-action hover:border-agent-action hover:bg-agent-action/5',
        ghost: 'text-muted-foreground hover:bg-agent-action/5 hover:text-foreground',
    };

const ITEM_TONE_CLASSES = {
    neutral: 'bg-muted-foreground/45',
    success: 'bg-emerald-700 dark:bg-emerald-500',
    warning: 'bg-amber-500',
    danger: 'bg-rose-600 dark:bg-rose-500',
} as const;

function ActionButtons({
    actions,
    disabled,
    onAction,
}: {
    readonly actions: ReadonlyArray<
        Extract<
            UserQuestionPresentation,
            { readonly type: 'actions' | 'message_preview' }
        >['actions'][number]
    >;
    readonly disabled: boolean;
    readonly onAction: (value: string, label: string) => void;
}) {
    return (
        <div className="flex flex-wrap items-center gap-2 pt-1">
            {actions.map((action) => (
                <Button
                    key={action.value}
                    type="button"
                    variant={
                        action.style === 'primary'
                            ? 'default'
                            : action.style === 'secondary'
                              ? 'outline'
                              : 'ghost'
                    }
                    className={cn(
                        'min-h-10 rounded-full px-5 text-sm shadow-none',
                        ACTION_STYLE_CLASSES[action.style],
                    )}
                    disabled={disabled}
                    onClick={() => onAction(action.value, action.label)}
                >
                    {action.label}
                </Button>
            ))}
        </div>
    );
}

function DecisionPrompt({
    question,
    description,
}: {
    readonly question: string;
    readonly description: string | null;
}) {
    return (
        <div className="space-y-2">
            {description ? (
                <Markdown className="text-foreground text-sm leading-relaxed">
                    {description}
                </Markdown>
            ) : null}
            <Markdown className="text-foreground text-sm leading-relaxed font-medium">
                {question}
            </Markdown>
        </div>
    );
}

function ActionPresentation({
    presentation,
    question,
}: {
    readonly presentation: Extract<
        UserQuestionPresentation,
        { readonly type: 'actions' }
    >;
    readonly question: string;
}) {
    return (
        <div className="space-y-2">
            {presentation.title ? (
                <Markdown className="text-foreground text-sm leading-relaxed font-semibold">
                    {presentation.title}
                </Markdown>
            ) : null}
            <DecisionPrompt
                question={question}
                description={presentation.description}
            />
        </div>
    );
}

function ListPresentation({
    presentation,
    question,
    disabled,
    selectedValues,
    onSelect,
}: {
    readonly presentation: Extract<
        UserQuestionPresentation,
        { readonly type: 'list' }
    >;
    readonly question: string;
    readonly disabled: boolean;
    readonly selectedValues: ReadonlyArray<string>;
    readonly onSelect: (value: string, label: string) => void;
}) {
    const [expanded, setExpanded] = useState(false);
    const visibleItems = expanded
        ? presentation.items
        : presentation.items.slice(0, presentation.initialVisibleCount);
    const hiddenItemCount = presentation.items.length - visibleItems.length;
    const hasDisclosure =
        presentation.items.length > presentation.initialVisibleCount;

    return (
        <div className="space-y-3">
            <Markdown className="text-foreground text-sm leading-relaxed font-semibold">
                {presentation.title}
            </Markdown>
            <div className="border-border/80 overflow-hidden rounded-lg border">
                <ul aria-label={presentation.title}>
                    {visibleItems.map((item) => (
                        <li
                            key={item.id}
                            className={cn(
                                'border-border/70 flex items-start gap-3 border-b px-3 py-2.5 last:border-b-0',
                                selectedValues.includes(item.id) &&
                                    'bg-agent-action/5',
                            )}
                        >
                            <V3RowSelectionCheckbox
                                label={`Select ${item.title}`}
                                selected={selectedValues.includes(item.id)}
                                disabled={disabled}
                                onChange={() => onSelect(item.id, item.title)}
                            />
                            <button
                                type="button"
                                className="flex min-w-0 flex-1 items-start gap-3 text-left disabled:opacity-50"
                                aria-pressed={selectedValues.includes(item.id)}
                                disabled={disabled}
                                onClick={() => onSelect(item.id, item.title)}
                            >
                                <span
                                    className={cn(
                                        'mt-1.5 size-2 shrink-0 rounded-full',
                                        ITEM_TONE_CLASSES[item.tone],
                                    )}
                                    aria-hidden={true}
                                />
                                <span className="min-w-0 flex-1 sm:flex sm:items-baseline sm:gap-3">
                                    <span className="text-foreground text-sm font-semibold">
                                        {item.title}
                                    </span>
                                    {item.detail ? (
                                        <Markdown className="text-muted-foreground min-w-0 text-sm leading-relaxed">
                                            {item.detail}
                                        </Markdown>
                                    ) : null}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
                {hasDisclosure ? (
                    <button
                        type="button"
                        className="text-agent-action hover:bg-agent-action/5 focus-visible:ring-agent-action/35 flex min-h-10 w-full items-center justify-between border-t px-3 text-left text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
                        aria-expanded={expanded}
                        onClick={() => setExpanded((current) => !current)}
                    >
                        <span>
                            {expanded
                                ? 'Show fewer'
                                : `+ ${hiddenItemCount} more`}
                        </span>
                        {expanded ? (
                            <ChevronUp className="size-4" aria-hidden={true} />
                        ) : (
                            <ChevronDown
                                className="size-4"
                                aria-hidden={true}
                            />
                        )}
                    </button>
                ) : null}
            </div>
            <DecisionPrompt
                question={question}
                description={presentation.description}
            />
        </div>
    );
}

function MessagePreviewPresentation({
    presentation,
    question,
    editing,
    draftMessage,
    disabled,
    saving,
    onDraftMessageChange,
    onCancelEdit,
    onSaveEdit,
}: {
    readonly presentation: Extract<
        UserQuestionPresentation,
        { readonly type: 'message_preview' }
    >;
    readonly question: string;
    readonly editing: boolean;
    readonly draftMessage: string;
    readonly disabled: boolean;
    readonly saving: boolean;
    readonly onDraftMessageChange: (message: string) => void;
    readonly onCancelEdit: () => void;
    readonly onSaveEdit: () => void;
}) {
    const hiddenRecipientCount = Math.max(
        presentation.recipientCount - presentation.recipients.length,
        0,
    );
    return (
        <div className="space-y-3">
            <div className="border-agent-action/35 rounded-lg border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-agent-action flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase">
                        <MessageSquareText
                            className="size-4"
                            aria-hidden={true}
                        />
                        Staged — not sent
                    </p>
                    <p className="text-muted-foreground text-sm">
                        to {presentation.recipientCount}{' '}
                        {presentation.recipientNoun}
                    </p>
                </div>
                {editing ? (
                    <div className="mt-3 space-y-2">
                        <label
                            htmlFor="structured-message-draft"
                            className="text-foreground text-sm font-semibold"
                        >
                            Edit message
                        </label>
                        <Textarea
                            id="structured-message-draft"
                            aria-label="Message draft"
                            value={draftMessage}
                            onChange={(event) =>
                                onDraftMessageChange(event.currentTarget.value)
                            }
                            disabled={disabled || saving}
                            className="min-h-28 resize-y"
                        />
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={disabled || saving}
                                onClick={onCancelEdit}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                className={cn(
                                    'rounded-full shadow-none',
                                    ACTION_STYLE_CLASSES.primary,
                                )}
                                disabled={
                                    disabled ||
                                    saving ||
                                    draftMessage.trim().length === 0
                                }
                                onClick={onSaveEdit}
                            >
                                {saving ? (
                                    <Loader2
                                        className="size-4 animate-spin motion-reduce:animate-none"
                                        aria-hidden={true}
                                    />
                                ) : null}
                                Save changes
                            </Button>
                        </div>
                    </div>
                ) : (
                    <blockquote className="text-foreground mt-3 text-sm leading-relaxed whitespace-pre-wrap italic">
                        {presentation.message}
                    </blockquote>
                )}
                <div
                    className="mt-3 flex flex-wrap gap-1.5"
                    aria-label="Recipients"
                >
                    {presentation.recipients.map((recipient, index) => (
                        <span
                            key={`${recipient}-${index}`}
                            className="bg-muted text-foreground rounded-full px-2.5 py-1 text-xs font-medium"
                        >
                            {recipient}
                        </span>
                    ))}
                    {hiddenRecipientCount > 0 ? (
                        <span className="bg-muted text-muted-foreground rounded-full px-2.5 py-1 text-xs font-medium">
                            + {hiddenRecipientCount} more
                        </span>
                    ) : null}
                </div>
            </div>
            {editing ? null : (
                <DecisionPrompt question={question} description={null} />
            )}
        </div>
    );
}

function PresentationContent({
    presentation,
    question,
    editingMessage,
    draftMessage,
    disabled,
    savingMessage,
    onDraftMessageChange,
    onCancelMessageEdit,
    onSaveMessageEdit,
    selectedListValues,
    onSelectListItem,
}: {
    readonly presentation: UserQuestionPresentation;
    readonly question: string;
    readonly editingMessage: boolean;
    readonly draftMessage: string;
    readonly disabled: boolean;
    readonly savingMessage: boolean;
    readonly onDraftMessageChange: (message: string) => void;
    readonly onCancelMessageEdit: () => void;
    readonly onSaveMessageEdit: () => void;
    readonly selectedListValues: ReadonlyArray<string>;
    readonly onSelectListItem: (value: string, label: string) => void;
}) {
    switch (presentation.type) {
        case 'actions':
            return (
                <ActionPresentation
                    presentation={presentation}
                    question={question}
                />
            );
        case 'list':
            return (
                <ListPresentation
                    presentation={presentation}
                    question={question}
                    disabled={disabled}
                    selectedValues={selectedListValues}
                    onSelect={onSelectListItem}
                />
            );
        case 'message_preview':
            return (
                <MessagePreviewPresentation
                    presentation={presentation}
                    question={question}
                    editing={editingMessage}
                    draftMessage={draftMessage}
                    disabled={disabled}
                    saving={savingMessage}
                    onDraftMessageChange={onDraftMessageChange}
                    onCancelEdit={onCancelMessageEdit}
                    onSaveEdit={onSaveMessageEdit}
                />
            );
        default:
            return null;
    }
}

export function ActionQuestionCard({
    presentation,
    question,
    disabled,
    onAction,
    onMessageEdit,
    onCancel,
    showActions = true,
}: {
    readonly presentation: UserQuestionPresentation;
    readonly question: string;
    readonly disabled: boolean;
    readonly onAction: (value: string | Array<string>, label: string) => void;
    readonly onMessageEdit?: (message: string) => Promise<void>;
    readonly onCancel?: () => void;
    readonly showActions?: boolean;
}) {
    const [editingMessage, setEditingMessage] = useState(false);
    const [draftMessage, setDraftMessage] = useState(
        presentation.type === 'message_preview' ? presentation.message : '',
    );
    const [savingMessage, setSavingMessage] = useState(false);
    const [selectedListValues, setSelectedListValues] = useState<Array<string>>(
        [],
    );
    const previewMessage =
        presentation.type === 'message_preview' ? presentation.message : '';

    useEffect(() => {
        setDraftMessage(previewMessage);
    }, [previewMessage]);

    useEffect(() => {
        if (showActions) {
            return;
        }
        setEditingMessage(false);
        setSavingMessage(false);
    }, [showActions]);

    const startMessageEdit = (value: string, label: string) => {
        if (
            presentation.type === 'message_preview' &&
            onMessageEdit &&
            value.trim().toLowerCase() === 'edit'
        ) {
            setEditingMessage(true);
            return;
        }
        onAction(value, label);
    };

    const cancelMessageEdit = () => {
        if (presentation.type === 'message_preview') {
            setDraftMessage(presentation.message);
        }
        setEditingMessage(false);
    };

    const saveMessageEdit = async () => {
        const message = draftMessage.trim();
        if (!onMessageEdit || !message || savingMessage) {
            return;
        }
        setSavingMessage(true);
        try {
            await onMessageEdit(message);
        } catch {
            // The caller owns the user-facing error. Keep the edited draft in
            // place so the coordinator can retry without retyping it.
            setSavingMessage(false);
        }
    };

    const selectListItem = (value: string, label: string) => {
        if (presentation.type !== 'list') {
            return;
        }
        if (presentation.selection === 'single') {
            setSelectedListValues([value]);
            onAction(value, label);
            return;
        }
        setSelectedListValues((current) =>
            current.includes(value)
                ? current.filter((entry) => entry !== value)
                : [...current, value],
        );
    };

    return (
        <div
            data-testid="structured-question-card"
            className="animate-in fade-in-0 slide-in-from-bottom-1 w-full max-w-2xl space-y-3 duration-200 motion-reduce:animate-none"
        >
            <PresentationContent
                presentation={presentation}
                question={question}
                editingMessage={editingMessage}
                draftMessage={draftMessage}
                disabled={disabled}
                savingMessage={savingMessage}
                onDraftMessageChange={setDraftMessage}
                onCancelMessageEdit={cancelMessageEdit}
                onSaveMessageEdit={() => {
                    void saveMessageEdit();
                }}
                selectedListValues={selectedListValues}
                onSelectListItem={selectListItem}
            />
            {showActions && !editingMessage && presentation.type === 'list' ? (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                    {presentation.selection === 'multiple' ? (
                        <Button
                            type="button"
                            className={cn(
                                'min-h-10 rounded-full px-5 text-sm shadow-none',
                                ACTION_STYLE_CLASSES.primary,
                            )}
                            disabled={
                                disabled || selectedListValues.length === 0
                            }
                            onClick={() =>
                                onAction(
                                    selectedListValues,
                                    `${selectedListValues.length} selected`,
                                )
                            }
                        >
                            Continue
                        </Button>
                    ) : null}
                    {onCancel ? (
                        <Button
                            type="button"
                            variant="ghost"
                            className="min-h-10 rounded-full px-5 text-sm"
                            disabled={disabled}
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                    ) : null}
                </div>
            ) : showActions &&
              !editingMessage &&
              (presentation.type === 'actions' ||
                  presentation.type === 'message_preview') ? (
                <ActionButtons
                    actions={presentation.actions}
                    disabled={disabled}
                    onAction={startMessageEdit}
                />
            ) : null}
        </div>
    );
}
